import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createRule, suppressRuleSuggestion } from '../../lib/api';

/**
 * Shown after a category PATCH returns `suggest_rule`. Offers to auto-categorize
 * future receipts from this vendor.
 */
export default function RuleSuggestionModal({ colors, suggestion, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!suggestion) return null;
  const { vendor, category, count } = suggestion;

  const createRuleAction = async () => {
    setSaving(true); setError(null);
    try {
      await createRule(vendor, category);
      onClose();
    } catch (e) {
      setError(e?.detail || 'Could not create rule');
    } finally { setSaving(false); }
  };

  const suppressAction = async () => {
    setSaving(true); setError(null);
    try {
      await suppressRuleSuggestion(vendor);
      onClose();
    } catch (e) {
      setError(e?.detail || 'Could not suppress');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="rule-suggestion-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: colors.border }}>
          <h2 className="text-sm font-bold" style={{ color: colors.text }}>Auto-categorize future receipts?</h2>
          <button onClick={onClose} style={{ color: colors.textMuted }}><X size={16} /></button>
        </div>
        <div className="p-5">
          <p className="text-sm" style={{ color: colors.text }}>
            You&apos;ve categorized {count} receipts from <strong>{vendor}</strong> as <strong>{category}</strong>.
          </p>
          <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
            Create an auto-categorization rule so future receipts from this vendor go straight to {category}?
          </p>
          {error ? <p className="text-xs mt-2" style={{ color: colors.error }}>{error}</p> : null}
          <div className="mt-4 flex flex-col gap-2">
            <button
              data-testid="rule-suggest-create"
              disabled={saving}
              onClick={createRuleAction}
              className="w-full py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: colors.accent, opacity: saving ? 0.5 : 1 }}
            >
              Create rule
            </button>
            <button
              data-testid="rule-suggest-not-now"
              disabled={saving}
              onClick={onClose}
              className="w-full py-2 rounded-lg text-sm font-semibold border"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}
            >
              Not now
            </button>
            <button
              data-testid="rule-suggest-never"
              disabled={saving}
              onClick={suppressAction}
              className="w-full py-2 rounded-lg text-xs font-semibold"
              style={{ color: colors.textMuted, backgroundColor: 'transparent' }}
            >
              Never suggest for {vendor}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
